import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Fuse from 'fuse.js';

import {
  CustomApptFieldsTemplate,
  StopTypes,
  WarehouseSettings,
} from 'types/Appointment';
import { LoadStopsItem, LoadStopsItemTypes, NormalizedLoad } from 'types/Load';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { Warehouse } from 'types/Warehouse';
import { TMS } from 'types/enums/Integrations';
import { normalizeTimezone } from 'utils/normalizeTimezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const fuseDropdownOptions = {
  threshold: 0.7,
  isCaseSensitive: false,
} as const;

function searchFuseDropdown(
  dropDownValues: string[],
  query: MaybeUndef<string>
): Undef<string> {
  const normalizedQuery = query?.trim() ?? '';
  if (!normalizedQuery) {
    return undefined;
  }

  const fuse = new Fuse(dropDownValues, fuseDropdownOptions);
  const result = fuse.search(normalizedQuery);
  return result.length > 0 ? result[0].item : undefined;
}

export const SevenElevenVirginiaID = '9b1beeac-c174-4282-860e-36eac63779c1';
export const SevenElevenMarylandID = '04782838-7a73-423f-9df5-67df700bdb75';

export enum TrailerType {
  Van = 'Cargo Van',
  Trailer = 'LTL/FTL Trailer',
  Passenger = 'Passenger Vehicle',
  Truck = 'Straight Truck',
}

// If user is rescheduling appt, exclude previous OpenDock info from notes of new appt
export function removeSchedulerInfo(input: string): string {
  return input.replace(/Opendock \b\d+\b\.?/i, '').trim();
}

export interface OpendockSearchWindow {
  startDateTime: Date;
  endDateTime: Date;
  isAiSuggested: boolean;
}

// From/To are one search range. Re-sync both when defaultSearchWindow changes,
// or skip entirely if the user edited either side (avoids mixed/inverted windows).
// Always re-sync when switching Pickup/Dropoff so each stop anchors on its own date.
export function shouldResyncOpendockSearchWindow(
  isStartDateTimeDirty: Undef<boolean>,
  isEndDateTimeDirty: Undef<boolean>,
  hasStopTypeChanged = false
): boolean {
  if (hasStopTypeChanged) {
    return true;
  }

  return !isStartDateTimeDirty && !isEndDateTimeDirty;
}

interface OpendockStopDate {
  date: Maybe<Date>;
  // True when the source field is a calendar date stored as UTC midnight
  // (e.g. carrier.pickupStart), not a full appointment datetime.
  isDateOnly: boolean;
}

// Treat Go zero timestamps (0001-01-01T00:00:00Z) as absent so real later candidates win.
function toValidDate(value: MaybeUndef<Date | string>): Maybe<Date> {
  if (value == null || value === '') {
    return null;
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime()) || parsed.getUTCFullYear() <= 1) {
    return null;
  }

  return parsed;
}

function firstValidOpendockStopDate(
  candidates: Array<{
    value: MaybeUndef<Date | string>;
    isDateOnly: boolean;
  }>
): OpendockStopDate {
  for (const candidate of candidates) {
    const date = toValidDate(candidate.value);
    if (date) {
      const resolved: OpendockStopDate = {
        date,
        isDateOnly: candidate.isDateOnly,
      };
      return resolved;
    }
  }

  const noStopDate: OpendockStopDate = { date: null, isDateOnly: false };
  return noStopDate;
}

// Pickup or delivery/dropoff row from load.stops[], shared by scheduling helpers.
function findSchedulingStop(
  load: NormalizedLoad,
  stopType: StopTypes
): Undef<LoadStopsItem> {
  return load.stops?.find((stop) =>
    stopType === StopTypes.Dropoff
      ? stop.stopType === LoadStopsItemTypes.Delivery ||
        stop.stopType === LoadStopsItemTypes.Dropoff
      : stop.stopType === LoadStopsItemTypes.Pickup
  );
}

// Resolve the best stop date for Opendock search defaults across TMS field shapes.
// Expects a NormalizedLoad (from normalizeLoad), so timezone-agnostic TMS dates have
// already been shifted by normalizeUTCDate for browser-local display.
export function resolveOpendockStopDate(
  load: NormalizedLoad,
  stopType: StopTypes
): OpendockStopDate {
  const schedulingStop = findSchedulingStop(load, stopType);

  if (stopType === StopTypes.Pickup) {
    // Priority: stop appointment times, then carrier date-only window, then stops[].
    return firstValidOpendockStopDate([
      { value: load.pickup.readyTime, isDateOnly: false },
      { value: load.pickup.apptStartTime, isDateOnly: false },
      { value: load.carrier.pickupStart, isDateOnly: true },
      { value: schedulingStop?.readyTime, isDateOnly: false },
      { value: schedulingStop?.apptStartTime, isDateOnly: false },
    ]);
  }

  // Priority: consignee delivery/appt times, then carrier dates, then stops[].
  return firstValidOpendockStopDate([
    { value: load.consignee.mustDeliver, isDateOnly: false },
    { value: load.consignee.apptStartTime, isDateOnly: false },
    { value: load.carrier.deliveryStart, isDateOnly: true },
    { value: load.carrier.expectedDeliveryTime, isDateOnly: false },
    { value: schedulingStop?.mustDeliver, isDateOnly: false },
    { value: schedulingStop?.apptStartTime, isDateOnly: false },
  ]);
}

// Default Opendock slot search range: stop date through stop date + 1 day when known.
// Dates must match OpendockForm's convertInputToWarehouseTimezone convention:
// local Date components represent the timezone used for the search window.
export function getOpendockDefaultSearchWindow(
  stopDate: Maybe<Date>,
  now: Date = new Date(),
  warehouseTimezone: MaybeUndef<string> = 'UTC',
  isLoadTimezoneAgnostic = false,
  isDateOnly = false
): OpendockSearchWindow {
  const tz = normalizeTimezone(warehouseTimezone) ?? 'UTC';
  const today = dayjs(now).tz(tz).startOf('day');
  // Opendock date pickers read/write local Date components as warehouse wall clock.
  // convertInputToWarehouseTimezone later turns these into real instants for the API.
  const toInputDate = (value: dayjs.Dayjs) =>
    new Date(
      value.year(),
      value.month(),
      value.date(),
      value.hour(),
      value.minute(),
      value.second(),
      value.millisecond()
    );
  const fallback = (): OpendockSearchWindow => {
    // No usable stop date (missing, invalid, or in the past): today through today + 7.
    const fallbackWindow: OpendockSearchWindow = {
      startDateTime: toInputDate(today),
      endDateTime: toInputDate(today.add(7, 'day').endOf('day')),
      isAiSuggested: false,
    };
    return fallbackWindow;
  };

  if (!stopDate || Number.isNaN(stopDate.getTime())) {
    return fallback();
  }

  // Pick the calendar day the user actually sees, then anchor it in the warehouse zone.
  //   - Timezone-agnostic TMSes (Aljex/Tai/QuantumEdge): OpendockForm has already run
  //     these through normalizeUTCDate, which shifts the instant so the BROWSER-LOCAL
  //     wall clock matches the TMS. Read the local calendar day, not the UTC one.
  //   - Date-only fields on timezone-aware TMSes (e.g. carrier.pickupStart): raw UTC
  //     midnights, so read the UTC calendar day.
  //   - Everything else is a true instant, convert it into the warehouse zone.
  let stopDay: dayjs.Dayjs;
  if (isLoadTimezoneAgnostic) {
    // normalizeUTCDate shifted the instant, dayjs(stopDate) recovers the wall clock.
    stopDay = dayjs.tz(dayjs(stopDate).format('YYYY-MM-DD'), tz).startOf('day');
  } else if (isDateOnly) {
    // Raw UTC midnight from the API, keep the UTC calendar day, not local.
    stopDay = dayjs
      .tz(dayjs.utc(stopDate).format('YYYY-MM-DD'), tz)
      .startOf('day');
  } else {
    // convert to the warehouse zone and take that calendar day.
    stopDay = dayjs(stopDate).tz(tz).startOf('day');
  }

  if (stopDay.isBefore(today)) {
    return fallback();
  }

  const searchWindow: OpendockSearchWindow = {
    startDateTime: toInputDate(stopDay),
    endDateTime: toInputDate(stopDay.add(1, 'day').endOf('day')),
    isAiSuggested: true,
  };
  return searchWindow;
}

/**
 * PO numbers for appointment scheduling on this stop (pickup or dropoff).
 *
 * Circle / TransportPro stores POs in special-instructions, the backend parses them at
 * ingest into stops[].poNumbers. TransportPro is Circle-only today.
 *
 * Resolution order matches backend SchedulingPONumbers (no stop refNumber here;
 * callers chain load.customer.refNumber and stop.refNumber after this):
 * pickup/consignee extractedPoNums (before save) -> stops[].poNumbers -> load.poNums
 */
export function schedulingPoNums(
  load: NormalizedLoad,
  stopType: StopTypes
): string {
  const stop = stopType === StopTypes.Dropoff ? load.consignee : load.pickup;
  const schedulingStop = findSchedulingStop(load, stopType);
  const fromStops =
    schedulingStop?.poNumbers?.length && schedulingStop.poNumbers.join(',');
  return stop.extractedPoNums?.trim() || fromStops || load.poNums?.trim() || '';
}

/**
 * Pallet count for appointment scheduling on this stop (pickup or dropoff).
 *
 * Circle note lines include PAL/CAS counts; backend sums them into stops[].palletCount.
 * Falls back to load-level totalOutPalletCount for other TMSes or older loads.
 *
 * Returns null when no pallet data is available so callers can distinguish an
 * explicit zero (real "zero pallets") from missing data and avoid falling back
 * to piece counts in that case.
 */
export function schedulingPalletCount(
  load: NormalizedLoad,
  stopType: StopTypes
): Maybe<number> {
  const stop = stopType === StopTypes.Dropoff ? load.consignee : load.pickup;
  const schedulingStop = findSchedulingStop(load, stopType);
  if (schedulingStop?.palletCount != null) {
    return schedulingStop.palletCount;
  }
  if (stop.extractedPalletCount && stop.extractedPalletCount > 0) {
    return stop.extractedPalletCount;
  }
  if (
    load.specifications.totalOutPalletCount &&
    load.specifications.totalOutPalletCount > 0
  ) {
    return load.specifications.totalOutPalletCount;
  }
  return null;
}

// Legacy code to AI parse TMS page beore we had the TMS API.
// TODO: Move AI-filling custom appt fields to backend and capture feedback in Braintrust
export function aiSuggestCustomApptFields(
  load: NormalizedLoad,
  stopType: StopTypes,
  warehouse: Warehouse,
  customFields: CustomApptFieldsTemplate[],
  tmsName?: string
) {
  const fieldMap = new Map<string, CustomApptFieldsTemplate>();
  customFields &&
    customFields.forEach((field) => fieldMap.set(field.name, field));

  const setField = (fieldName: string, value: string) => {
    const field = fieldMap.get(fieldName);
    if (field) {
      field.value = value;
    }
  };

  switch (warehouse.warehouseID) {
    case SevenElevenVirginiaID:
      setField('strTrailer', load.carrier.trailerNumber);
      setField('dropdownVehicleType', TrailerType.Truck);
      setField(
        'intMisc1',
        load.specifications.totalOutPalletCount?.toString() ?? ''
      );
      setField('dropdownOrder Type', 'PO');

      load.poNums &&
        load.poNums.split(',').forEach((poNum, index) => {
          const i = index + 1;
          setField(poFieldName(i), poNum);
        });
      break;

    case SevenElevenMarylandID:
      setField('phoneCell Phone', load.carrier.firstDriverPhone);
      setField('strHauling Carrier Name', load.carrier.name);
      break;

    default: {
      if (tmsName === TMS.TransportPro) {
        break;
      }

      const stop =
        stopType === StopTypes.Dropoff ? load.consignee : load.pickup;

      const refNum =
        schedulingPoNums(load, stopType) ||
        load.customer.refNumber ||
        stop.refNumber;
      const palletCount = schedulingPalletCount(load, stopType);

      customFields &&
        customFields.forEach((field) => {
          const lowerLabel = field.label.toLowerCase();
          const lowerDescription = field.description.toLowerCase();

          const isMatch = (term: string): boolean => {
            return lowerLabel.includes(term) || lowerDescription.includes(term);
          };

          // Handle non-dropdown fields
          if (field.requiredForCarrier && field.type !== 'dropdown') {
            if (isMatch('pallet')) {
              field.value =
                palletCount != null
                  ? palletCount.toString()
                  : (load.specifications.totalPieces?.val?.toString() ?? '');
              return;
            }

            // AI-fill phone number, if present
            // TODO: more nuanced/specific phone number filling
            if (phoneTerms.some((term) => isMatch(term))) {
              field.value = load.carrier.phone ?? '';
              return;
            }

            // AI-fill carrier name, if present
            if (carrierTerms.some((term) => isMatch(term))) {
              field.value = load.carrier.name;
              return;
            }

            // AI-fill customer name, if present
            if (customerTerms.some((term) => isMatch(term))) {
              field.value = load.customer.name ?? load.pickup.name;
              return;
            }

            // AI-fill PO number, if present
            if (poTerms.some((term) => isMatch(term))) {
              field.value = refNum;
              return;
            }

            // AI-fill PRO number, if present
            if (proTerms.some((term) => isMatch(term))) {
              field.value =
                (lowerLabel.toUpperCase().includes('PRO OR BOL') ||
                  field.label.toUpperCase().includes('BOL OR PRO')) &&
                warehouse.warehouseName.toLowerCase().startsWith('om')
                  ? load.customer.refNumber
                  : load.freightTrackingID;
              return;
            }
          } else if (
            // Handle dropdown fields
            field.requiredForCarrier &&
            field.type === 'dropdown' &&
            field.dropDownValues &&
            field.dropDownValues?.length > 0
          ) {
            if (carrierTerms.some((term) => isMatch(term))) {
              const match = searchFuseDropdown(
                field.dropDownValues,
                load.carrier.name
              );
              if (match) {
                field.value = match;
                return;
              }
            }

            if (customerTerms.some((term) => isMatch(term))) {
              const match = searchFuseDropdown(
                field.dropDownValues,
                load.customer.name ?? load.pickup.name
              );
              if (match) {
                field.value = match;
              }
            }
          }
        });
    }
  }
}

const poTerms = [
  'po#',
  'po #',
  'po num',
  'po number',
  'po numbers',
  'purchase order',
  'order number',
];

const proTerms = [
  'pro',
  'tracking number',
  'load number',
  'load id',
  'load#',
  'load #',
  'shipment id',
  'id#',
  'id #',
  'shipment#',
  'shipment #',
];

const carrierTerms = ['carrier'];

const phoneTerms = ['phone'];

// REVIEW: Is vendor customer/pickup or carrier? Based on OpenDock 7/11 form, seems to be customer/pickup
const customerTerms = ['customer', 'vendor', 'supplier'];
// AI-fills only *required* PO # fields
export function suggestPONumCustomFields(
  customApptFields: CustomApptFieldsTemplate[],
  poNum: string
) {
  if (!poNum) {
    return;
  }

  poTerms.forEach((term) => {
    customApptFields?.map((field) => {
      if (
        (field.label.toLowerCase().includes(term) ||
          field.description.toLowerCase().includes(term)) &&
        field.requiredForCarrier &&
        (field.type !== 'dropdown' || field.dropDownValues?.length === 0) &&
        field.value === ''
      ) {
        field.value = poNum;
      }
      return field;
    });
  });
}

// AI-fills only *required* PRO # fields
export function suggestPROCustomFields(
  customApptFields: CustomApptFieldsTemplate[],
  freightTrackingID: Undef<string>,
  externalTMSID: Undef<string>
) {
  if (!freightTrackingID) {
    return;
  }

  proTerms.forEach((term) => {
    customApptFields?.map((field) => {
      if (
        (field.label.toLowerCase().includes(term) ||
          field.description.toLowerCase().includes(term)) &&
        field.requiredForCarrier &&
        (field.type !== 'dropdown' || field.dropDownValues?.length === 0)
      ) {
        if (field.label.toUpperCase().includes('PRO OR BOL') && externalTMSID) {
          field.value = externalTMSID;
        } else {
          field.value = freightTrackingID;
        }

        return field;
      }

      return field;
    });
  });
}

// AI-fills reference number if OpenDock warehouse makes it visible
export function suggestRefNumberField(
  settings: WarehouseSettings,
  poNum: string,
  pro: Undef<string>,
  tmsName?: string
): string {
  if (!settings.referenceNumberIsVisible) {
    return '';
  }

  const suggestPRO = proTerms.some((term) => {
    return (
      settings.referenceNumberHelperText.toLowerCase().includes(term) ||
      settings.referenceNumberDisplayName.toLowerCase().includes(term)
    );
  });

  if (suggestPRO) {
    return pro ?? '';
  }

  const suggestPONum = poTerms.some((term) => {
    return (
      settings.referenceNumberHelperText.toLowerCase().includes(term) ||
      settings.referenceNumberDisplayName.toLowerCase().includes(term)
    );
  });

  if (suggestPONum) {
    return poNum ?? '';
  }

  if (tmsName === TMS.TransportPro) {
    return '';
  }

  return pro ?? '';
}

// AI-fills *required* pallet count
export function suggestPalletCountField(
  customApptFields: CustomApptFieldsTemplate[],
  palletCount: MaybeUndef<number>
) {
  if (!palletCount || palletCount < 1) {
    return;
  }

  customApptFields?.map((field) => {
    if (
      (field.label.toLowerCase().includes('pallet') ||
        field.description.toLowerCase().includes('pallet')) &&
      field.requiredForCarrier &&
      (field.type !== 'dropdown' || field.dropDownValues?.length === 0)
    ) {
      field.value = `${palletCount}`;
      return field;
    }

    return field;
  });
}

// Given the PO index (1-based), return the name of the associated form name.
// PO field names for warehouse 8010 (NFI 7-11 VA) are as follows:
//
// "strOrder\d" for 40/50 PO form inputs
//
// "strOrder \d" for the other 10
function poFieldName(index: number): string {
  switch (index) {
    case 1:
    case 2:
    case 41:
    case 42:
    case 43:
    case 44:
    case 45:
    case 46:
    case 48:
    case 49:
      // Add space
      return 'strOrder ' + index;
    default:
      // No space
      return 'strOrder' + index;
  }
}

// Whitelist of TMSes that we support updating load with appointment details
const tmsUpdateLoadAppointmentSupportedTMSes: Record<TMS, boolean> = {
  [TMS.Turvo]: true,
  [TMS.Aljex]: true,
  [TMS.Relay]: true,
  [TMS.McleodEnterprise]: true,
  [TMS.QuantumEdge]: true,

  [TMS.Stark]: true,
  [TMS.TransportPro]: true,
  // TODO: support more TMS integrations
  [TMS.FreightFlow]: false,
  [TMS.Tai]: false,
  [TMS.Mcleod]: false,
  [TMS.GlobalTranzTMS]: false,
  [TMS.ThreeG]: false,
  [TMS.Revenova]: false,
  [TMS.MercurygateBoomi]: false,
  [TMS.PLS]: false,
};

export function isTMSUpdateLoadAppointmentSupported(tmsName: string): boolean {
  return tmsUpdateLoadAppointmentSupportedTMSes[tmsName as TMS] ?? false;
}
